export type CaptureRegion = {
  x: number | undefined;
  y: number | undefined;
  width: number | undefined;
  height: number | undefined;
};

export type NormalizedCaptureRegion = {
  x: number;
  y: number;
  width: number;
  height: number;
};

export type AxisAlignedRect = {
  left: number;
  top: number;
  right: number;
  bottom: number;
  width: number;
  height: number;
};

export type VideoCropRatios = {
  xRatio: number;
  yRatio: number;
  widthRatio: number;
  heightRatio: number;
  /** Unrotated intersection size in CSS pixels (source crop canvas). */
  sourceWidth: number;
  sourceHeight: number;
  /** Where that crop sits inside the screen-aligned selection (CSS pixels). */
  destX: number;
  destY: number;
  destWidth: number;
  destHeight: number;
};

/** Longest edge of a New Thumbnail bitmap; keeps uploads under ``MAX_USER_IMAGE_UPLOAD_BYTES``. */
export const CAPTURE_CANVAS_MAX_EDGE_PX = 4096;

/** Explicit quality for capture WebP exports (browser default is too lossy on some engines). */
export const CAPTURE_WEBP_QUALITY = 0.92;

/**
 * Size a capture canvas at source-pixel resolution (``cssSize * scale``),
 * clamping the long edge so a zoomed-in crop cannot blow the upload budget.
 */
export function captureCanvasSize(
  cssWidth: number,
  cssHeight: number,
  scaleX: number,
  scaleY: number,
): { width: number; height: number } {
  let width = Math.max(1, Math.round(cssWidth * scaleX));
  let height = Math.max(1, Math.round(cssHeight * scaleY));
  const longEdge = Math.max(width, height);
  if (longEdge > CAPTURE_CANVAS_MAX_EDGE_PX) {
    const clamp = CAPTURE_CANVAS_MAX_EDGE_PX / longEdge;
    width = Math.max(1, Math.round(width * clamp));
    height = Math.max(1, Math.round(height * clamp));
  }
  return { width, height };
}

export function normalizeCaptureRegion(cap: CaptureRegion): NormalizedCaptureRegion {
  const x = cap.x ?? 0;
  const y = cap.y ?? 0;
  const width = cap.width ?? 0;
  const height = cap.height ?? 0;
  return {
    x: width < 0 ? x + width : x,
    y: height < 0 ? y + height : y,
    width: Math.abs(width),
    height: Math.abs(height),
  };
}

export function normalizeRotationDeg(rotationDeg: number | undefined): number {
  const wrapped = ((rotationDeg ?? 0) % 360 + 360) % 360;
  if (wrapped === 90 || wrapped === 180 || wrapped === 270) return wrapped;
  return 0;
}

/**
 * Layout box of the decoded picture inside an ``object-fit: contain`` video.
 * Crop math has to use this, not the element's full border box, or letterbox
 * padding maps the selection onto the wrong source pixels (often empty).
 */
export function videoContentBoxFromRect(
  rect: AxisAlignedRect,
  mediaWidth: number,
  mediaHeight: number,
): AxisAlignedRect {
  if (!mediaWidth || !mediaHeight || !rect.width || !rect.height) {
    return rect;
  }
  const mediaRatio = mediaWidth / mediaHeight;
  const boxRatio = rect.width / rect.height;
  if (boxRatio > mediaRatio) {
    const width = rect.height * mediaRatio;
    const left = rect.left + (rect.width - width) / 2;
    return { left, top: rect.top, width, height: rect.height, right: left + width, bottom: rect.top + rect.height };
  }
  const height = rect.width / mediaRatio;
  const top = rect.top + (rect.height - height) / 2;
  return { left: rect.left, top, width: rect.width, height, right: rect.left + rect.width, bottom: top + height };
}

export function videoContentBox(video: HTMLVideoElement): AxisAlignedRect {
  const rect = video.getBoundingClientRect();
  return videoContentBoxFromRect(
    {
      left: rect.left,
      top: rect.top,
      right: rect.right,
      bottom: rect.bottom,
      width: rect.width,
      height: rect.height,
    },
    video.videoWidth,
    video.videoHeight,
  );
}

/** CSS ``rotate()`` (clockwise, y-down) around a center. */
export function rotatePoint(
  x: number,
  y: number,
  centerX: number,
  centerY: number,
  rotationDeg: number,
): { x: number; y: number } {
  const rotation = normalizeRotationDeg(rotationDeg);
  const dx = x - centerX;
  const dy = y - centerY;
  if (rotation === 90) {
    return { x: centerX - dy, y: centerY + dx };
  }
  if (rotation === 180) {
    return { x: centerX - dx, y: centerY - dy };
  }
  if (rotation === 270) {
    return { x: centerX + dy, y: centerY - dx };
  }
  return { x, y };
}

/** Inverse of CSS ``rotate()`` (clockwise, y-down) around a center. */
export function unrotatePoint(
  x: number,
  y: number,
  centerX: number,
  centerY: number,
  rotationDeg: number,
): { x: number; y: number } {
  const rotation = normalizeRotationDeg(rotationDeg);
  const dx = x - centerX;
  const dy = y - centerY;
  if (rotation === 90) {
    // inverse of (x, y) -> (-y, x)
    return { x: centerX + dy, y: centerY - dx };
  }
  if (rotation === 180) {
    return { x: centerX - dx, y: centerY - dy };
  }
  if (rotation === 270) {
    // inverse of (x, y) -> (y, -x)
    return { x: centerX - dy, y: centerY + dx };
  }
  return { x, y };
}

/**
 * Unrotated layout box for an element whose CSS ``rotate()`` is applied around
 * its center. ``getBoundingClientRect()`` is the AABB of the rotated box.
 */
export function unrotatedLayoutBoxFromAabb(
  aabb: AxisAlignedRect,
  rotationDeg: number,
): AxisAlignedRect {
  const rotation = normalizeRotationDeg(rotationDeg);
  const centerX = aabb.left + aabb.width / 2;
  const centerY = aabb.top + aabb.height / 2;
  const layoutWidth = rotation === 90 || rotation === 270 ? aabb.height : aabb.width;
  const layoutHeight = rotation === 90 || rotation === 270 ? aabb.width : aabb.height;
  const left = centerX - layoutWidth / 2;
  const top = centerY - layoutHeight / 2;
  return {
    left,
    top,
    width: layoutWidth,
    height: layoutHeight,
    right: left + layoutWidth,
    bottom: top + layoutHeight,
  };
}

function intersectRects(a: AxisAlignedRect, b: AxisAlignedRect): AxisAlignedRect | null {
  const left = Math.max(a.left, b.left);
  const top = Math.max(a.top, b.top);
  const right = Math.min(a.right, b.right);
  const bottom = Math.min(a.bottom, b.bottom);
  const width = right - left;
  const height = bottom - top;
  if (width <= 0 || height <= 0) return null;
  return { left, top, right, bottom, width, height };
}

/**
 * Map a root-relative capture selection onto unrotated video source ratios,
 * undoing CSS ``rotate()`` so a 90°/270° video still crops the pixels the user
 * framed on screen.
 */
export function videoCropFromSelection(args: {
  rootRect: AxisAlignedRect;
  videoAabb: AxisAlignedRect;
  mediaWidth: number;
  mediaHeight: number;
  selection: NormalizedCaptureRegion;
  rotationDeg?: number;
}): VideoCropRatios | null {
  const rotation = normalizeRotationDeg(args.rotationDeg);
  const layoutBox = unrotatedLayoutBoxFromAabb(args.videoAabb, rotation);
  const contentBox = videoContentBoxFromRect(layoutBox, args.mediaWidth, args.mediaHeight);
  const centerX = args.videoAabb.left + args.videoAabb.width / 2;
  const centerY = args.videoAabb.top + args.videoAabb.height / 2;

  const screenSelection: AxisAlignedRect = {
    left: args.rootRect.left + args.selection.x,
    top: args.rootRect.top + args.selection.y,
    width: args.selection.width,
    height: args.selection.height,
    right: args.rootRect.left + args.selection.x + args.selection.width,
    bottom: args.rootRect.top + args.selection.y + args.selection.height,
  };

  const corners = [
    unrotatePoint(screenSelection.left, screenSelection.top, centerX, centerY, rotation),
    unrotatePoint(screenSelection.right, screenSelection.top, centerX, centerY, rotation),
    unrotatePoint(screenSelection.left, screenSelection.bottom, centerX, centerY, rotation),
    unrotatePoint(screenSelection.right, screenSelection.bottom, centerX, centerY, rotation),
  ];
  const localLeft = Math.min(...corners.map((c) => c.x));
  const localTop = Math.min(...corners.map((c) => c.y));
  const localRight = Math.max(...corners.map((c) => c.x));
  const localBottom = Math.max(...corners.map((c) => c.y));
  const localSelection: AxisAlignedRect = {
    left: localLeft,
    top: localTop,
    right: localRight,
    bottom: localBottom,
    width: localRight - localLeft,
    height: localBottom - localTop,
  };

  const intersection = intersectRects(localSelection, contentBox);
  if (!intersection) return null;

  const screenCorners = [
    rotatePoint(intersection.left, intersection.top, centerX, centerY, rotation),
    rotatePoint(intersection.right, intersection.top, centerX, centerY, rotation),
    rotatePoint(intersection.left, intersection.bottom, centerX, centerY, rotation),
    rotatePoint(intersection.right, intersection.bottom, centerX, centerY, rotation),
  ];
  const screenLeft = Math.min(...screenCorners.map((c) => c.x));
  const screenTop = Math.min(...screenCorners.map((c) => c.y));
  const screenRight = Math.max(...screenCorners.map((c) => c.x));
  const screenBottom = Math.max(...screenCorners.map((c) => c.y));

  return {
    xRatio: (intersection.left - contentBox.left) / contentBox.width,
    yRatio: (intersection.top - contentBox.top) / contentBox.height,
    widthRatio: intersection.width / contentBox.width,
    heightRatio: intersection.height / contentBox.height,
    sourceWidth: intersection.width,
    sourceHeight: intersection.height,
    destX: screenLeft - screenSelection.left,
    destY: screenTop - screenSelection.top,
    destWidth: screenRight - screenLeft,
    destHeight: screenBottom - screenTop,
  };
}

export function selectionIntersectsVideo(args: {
  rootRect: AxisAlignedRect;
  videoAabb: AxisAlignedRect;
  mediaWidth: number;
  mediaHeight: number;
  selection: NormalizedCaptureRegion;
  rotationDeg?: number;
}): boolean {
  return videoCropFromSelection(args) != null;
}

/** Types whose painted region cannot be snapshotted client-side. */
const THUMBNAIL_CAPTURE_EXCLUDED_TYPES = new Set(['audio', 'website', 'youtube']);

/**
 * Whether New Thumbnail / Full Media | Thumbnail chrome is offered for this type.
 * Cross-origin iframe surfaces (`website`, `youtube`) cannot be read by
 * html2canvas; audio has no paintable region.
 */
export function mediaTypeSupportsThumbnailCapture(
  mediaType?: string | null,
): boolean {
  return !THUMBNAIL_CAPTURE_EXCLUDED_TYPES.has((mediaType ?? '').trim());
}

/**
 * True when the canvas has at least one non-transparent pixel. Used to block
 * saving a blank html2canvas / video crop (cross-origin iframe, unloaded frame).
 */
export function canvasHasVisiblePixels(
  canvas: HTMLCanvasElement | null | undefined,
): boolean {
  if (!canvas || canvas.width <= 0 || canvas.height <= 0) return false;
  const context = canvas.getContext('2d');
  if (!context) return false;
  try {
    const imageData = context.getImageData(0, 0, canvas.width, canvas.height).data;
    return imageData.some((channel, index) => index % 4 === 3 && channel > 0);
  } catch {
    return false;
  }
}

export function resolvePersistedCaptureRef(
  ...sources: Array<{ mediaContext?: { capture?: unknown } | null } | null | undefined>
): string {
  for (const source of sources) {
    const capture =
      typeof source?.mediaContext?.capture === 'string'
        ? source.mediaContext.capture.trim()
        : '';
    if (capture) return capture;
  }
  return '';
}

/** Parent wrote `capture: ''` (Use Full Media). Distinct from capture not loaded yet. */
export function isExplicitlyClearedCapture(
  item: { mediaContext?: { capture?: unknown } | null } | null | undefined,
): boolean {
  return typeof item?.mediaContext?.capture === 'string' && !resolvePersistedCaptureRef(item);
}

/**
 * Thumbnail to paint: a just-taken preview wins over staged/persisted so a new
 * crop is not hidden behind an older saved still.
 */
export function resolveCaptureDisplaySrc(args: {
  previewSrc?: string | null;
  stagedRef?: string | null;
  persistedRef?: string | null;
  resolveUrl: (ref: string) => string;
}): string {
  const preview = typeof args.previewSrc === 'string' ? args.previewSrc.trim() : '';
  if (preview) return preview;
  const staged = typeof args.stagedRef === 'string' ? args.stagedRef.trim() : '';
  if (staged) return args.resolveUrl(staged);
  const persisted = typeof args.persistedRef === 'string' ? args.persistedRef.trim() : '';
  if (persisted) return args.resolveUrl(persisted);
  return '';
}

export function persistableCaptureRef(value: string | null | undefined): string {
  const trimmed = typeof value === 'string' ? value.trim() : '';
  if (!trimmed) return '';
  if (trimmed.startsWith('data:') || trimmed.startsWith('blob:')) return '';
  return trimmed;
}

/**
 * URI to store on ``mediaContext.capture``. Preview/canvas data URLs are
 * display-only; persist the staged S3 reference or an already-saved remote
 * capture so event media does not keep a huge base64 payload.
 */
export function resolveCapturePersistSrc(args: {
  stagedRef?: string | null;
  persistedRef?: string | null;
}): string {
  return persistableCaptureRef(args.stagedRef) || persistableCaptureRef(args.persistedRef);
}

/**
 * Whether a compact preview (Displayed Media card, table/accordion thumb) paints
 * the saved thumbnail still. Image and document tiles show `mediaContext.capture`.
 * Video/audio tiles show a thumbnail only after Use Thumbnail writes it as the
 * still-frame poster. Fullscreen opened from that tile must start on the same
 * Full Media | Thumbnail side.
 */
export function mediaPreviewShowsCapture(item: {
  mediaType?: string | null;
  mediaContext?: {
    capture?: unknown;
    canvas?: { stillFrameImage?: unknown } | null;
  } | null;
} | null | undefined): boolean {
  const capture =
    typeof item?.mediaContext?.capture === 'string'
      ? item.mediaContext.capture.trim()
      : '';
  if (!capture) return false;
  if (item?.mediaType === 'video' || item?.mediaType === 'audio') {
    const stillFrameImage =
      typeof item?.mediaContext?.canvas?.stillFrameImage === 'string'
        ? item.mediaContext.canvas.stillFrameImage.trim()
        : '';
    return Boolean(stillFrameImage);
  }
  return true;
}

/**
 * Presentation's maximized viewer must confirm the crop. Other fullscreen
 * viewers save on mouse-up, except Displayed Media thumbnail edit.
 */
export function shouldShowCaptureRegionDialog(options: {
  isFullscreen: boolean;
  isThumbnailEditSession: boolean;
  confirmCaptureRegion: boolean;
}): boolean {
  return !options.isFullscreen || options.isThumbnailEditSession || options.confirmCaptureRegion;
}

export function shouldAutoSaveCaptureOnCommit(options: {
  isFullscreen: boolean;
  thumbnailEditMode: boolean;
  confirmCaptureRegion: boolean;
}): boolean {
  return options.isFullscreen && !options.thumbnailEditMode && !options.confirmCaptureRegion;
}

/**
 * Which surfaces feed ``confirmCaptureRegion`` to the app-level maximized
 * viewer. Presentation always has. The timeline event pop-up does too: without
 * it the horizontal timeline took the ``shouldAutoSaveCaptureOnCommit`` path and
 * saved on mouse-up, so the Capture Region pop-up never opened and the user had
 * no Save / Cancel step (TR-4903).
 */
export function maximizedViewerConfirmsCapture(options: {
  selectedView: string | null | undefined;
  isEventModalOpen: boolean;
}): boolean {
  return options.selectedView === 'presentation' || options.isEventModalOpen;
}

/** Tooltip copy keys the Remove capture trigger can show. */
export type CaptureRemovalTooltipKey =
  | 'media.editingDisabled'
  | 'media.captureRemove'
  | 'media.captureRemoveNone';

export type CaptureRemovalState = {
  enabled: boolean;
  tooltipKey: CaptureRemovalTooltipKey;
};

/**
 * Enabled state and tooltip for the Remove capture (trash) trigger, resolved
 * together so they cannot disagree.
 *
 * They used to: the button was disabled whenever the item had no capture, but
 * the tooltip always read "Remove the capture for this item". With nothing to
 * remove the icon therefore looked clickable and did nothing, with no reason
 * given (TR-4692).
 */
export function resolveCaptureRemovalState(options: {
  editingEnabled: boolean;
  /** A saved, staged, or just-cropped capture — whatever the pane is showing. */
  hasCaptureAvailable: boolean;
}): CaptureRemovalState {
  if (!options.editingEnabled) {
    return { enabled: false, tooltipKey: 'media.editingDisabled' };
  }
  if (!options.hasCaptureAvailable) {
    return { enabled: false, tooltipKey: 'media.captureRemoveNone' };
  }
  return { enabled: true, tooltipKey: 'media.captureRemove' };
}
